import { setCookie } from "@/actions";

import {
  pushSelectItemDataLayer,
  pushSelectPromotionDataLayer,
  pushViewPromotionDataLayer
} from "./ecommerce";
import { normalizePagePath } from "./helpers";

import type { EcommerceItem, SelectItemParams } from "./ecommerce";

export {
  buildPageviewPayload,
  getAnalyticsEnvironment,
  getAnalyticsUserData,
  normalizePagePath,
  pushPageviewDataLayer
} from "./pageview";

export { pushAddToCartDataLayer, pushSelectItemDataLayer } from "./ecommerce";

export type {
  AnalyticsEnvironment,
  PageData,
  PageviewPayload,
  SubscriberStatus,
  UserData
} from "./types";

export type {
  AddToCartDataLayerParams,
  EcommerceItem,
  SelectItemParams
} from "./ecommerce";

const AFFILIATION = "Cobasi";

type SelectItemProduct = {
  id: string;
  name: string;
  brandName?: string;
  brand?: string;
  skus?: Array<{
    id: string;
    name?: string;
    title?: string;
    seller?: {
      price?: number;
      listPrice?: number;
    };
  }>;
  price?: number;
  oldPrice?: number;
};

type SelectItemOptions = {
  product: SelectItemProduct;
  itemListName?: string;
  itemListId?: string;
  index?: number;
  selectedSkuId?: string;
};

const buildSelectItemPayload = ({
  product,
  itemListName,
  itemListId,
  index = 0,
  selectedSkuId
}: SelectItemOptions): SelectItemParams => {
  const path =
    typeof window !== "undefined"
      ? normalizePagePath(window.location.pathname)
      : "homepage";
  const sku =
    product.skus?.find((item) => item.id === selectedSkuId) ||
    product.skus?.[0];
  const price = sku?.seller?.price ?? product.price ?? 0;
  const listPrice = sku?.seller?.listPrice ?? product.oldPrice ?? 0;
  const discount = listPrice > price ? listPrice - price : 0;

  const items: EcommerceItem[] = [
    {
      item_id: String(sku?.id || product.id),
      item_name: product.name,
      price,
      quantity: 1,
      discount,
      affiliation: AFFILIATION,
      item_brand: product.brandName || product.brand || "",
      item_category: "",
      item_category2: "",
      item_category3: "",
      item_variant: sku?.name || sku?.title || "",
      ...(typeof index === "number" ? { index } : {})
    }
  ];

  return {
    itemListName: itemListName || path,
    itemListId: itemListId || path,
    items
  };
};

const selectItem = ({
  product,
  itemListName,
  itemListId,
  index,
  selectedSkuId
}: SelectItemOptions) => {
  const payload = buildSelectItemPayload({
    product,
    itemListName,
    itemListId,
    index,
    selectedSkuId
  });

  window.gtag("event", "select_item", {
    item_list_name: payload.itemListName,
    item_list_id: payload.itemListId,
    items: payload.items.map((item) => ({
      ...item,
      currency: "BRL"
    }))
  });

  pushSelectItemDataLayer(payload);
};

const selectPromotion = async (promotion_name: string) => {
  window.gtag("event", "select_promotion", {
    promotion_name
  });

  setCookie("lastSelectPromotionName", promotion_name, {
    path: "/",
    domain: `.${location.hostname.replace(/^www\./i, "")}`
  });

  pushSelectPromotionDataLayer(promotion_name);
};

const viewPromotion = async (promotion_name: string) => {
  window.gtag("event", "view_promotion", {
    promotion_name
  });

  pushViewPromotionDataLayer(promotion_name);
};

export { selectPromotion, viewPromotion, selectItem };
